<template>
    <CicPopup
        id="edit_delivery_point_group"
        :title="item.groupId ? 'Edit Delivery Point Group' : 'New Delivery Point Group'"
        :saveable="editing"
        class="no-padding"
        @blur="$emit('blur')"
        @input="$emit('input')"
    >
        <div class="item-form">
            <ul>
                <li class="form-label">
                    {{ $t('Name') }}
                </li>
                <li>
                    <CicTextbox
                        id="group_name"
                        :enabled="editing"
                        :value="item.name"
                        :maxlength="255"
                        @input="(val) => updateFieldValue('name', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('Routing Method') }}
                </li>
                <li>
                    <CicSelect
                        id="group_routing"
                        :enabled="editing"
                        :value="item.routing"
                        :options="[
                            { value: 'sequential', label: $t('Sequential') },
                            { value: 'weighted', label: $t('Weighted') },
                        ]"
                        @input="(val) => updateFieldValue('routing', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('Disabled') }}
                </li>
                <li>
                    <CicCheckbox
                        id="group_disabled"
                        :value="item.disabled"
                        @input="(val) => updateFieldValue('disabled', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('Destinations') }}
                </li>
                <li>
                    <div style="font-weight: normal; padding: 5px 0 5px 0; font-size: 11px">
                        {{
                            $t(
                                'Changes to a delivery point name, outbound number or max number of calls will affect all instances of the delivery point.',
                            )
                        }}
                    </div>
                </li>
            </ul>
        </div>
        <div>
            <table class="cic-table">
                <thead>
                    <tr>
                        <th>{{ $t('Delivery Point') }}</th>
                        <th>{{ $t('Outbound Number') }}</th>
                        <th>{{ $t('Max Number of Calls') }}</th>
                        <th>{{ $t('Percentage (%)') }}</th>
                        <template v-if="editing">
                            <th colspan="2">{{ $t('Priority') }}</th>
                            <th></th>
                        </template>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(member, memberIndex) in item.members">
                        <td>
                            <CicTextbox
                                :enabled="editing && member.allowed"
                                :value="member.name"
                                :maxlength="64"
                                @input="(val) => updateMember(memberIndex, 'name', val)"
                            />
                        </td>
                        <td>
                            <CicTextbox
                                :enabled="editing && member.allowed"
                                :value="member.number"
                                :maxlength="32"
                                @input="(val) => updateMember(memberIndex, 'number', val)"
                            />
                        </td>
                        <td>
                            <CicNumber
                                :enabled="editing && member.allowed"
                                :min="0"
                                :value="member.maxCalls"
                                @input="(val) => updateMember(memberIndex, 'maxCalls', val)"
                            />
                        </td>
                        <td>
                            <CicNumber
                                :enabled="editing"
                                :min="0"
                                :max="100"
                                :value="member.weight"
                                @input="(val) => updateMember(memberIndex, 'weight', val)"
                            />
                        </td>
                        <template v-if="editing">
                            <td>
                                <IconButton
                                    v-if="memberIndex < item.members.length - 1"
                                    :title="$t('Deprioritise')"
                                    icon="down-icon"
                                    class="el-icon-arrow-down"
                                    @click="move(memberIndex, 1)"
                                />
                            </td>
                            <td>
                                <IconButton
                                    v-if="memberIndex > 0"
                                    :title="$t('Prioritise')"
                                    icon="up-icon"
                                    class="el-icon-arrow-up"
                                    @click="move(memberIndex, -1)"
                                />
                            </td>
                            <td>
                                <IconButton
                                    v-if="item.members.length > 1"
                                    icon="remove-icon"
                                    @click="removeMember(memberIndex)"
                                />
                            </td>
                        </template>
                    </tr>
                    <tr v-if="editing">
                        <td>
                            <CicSelect
                                id="group_new_point"
                                :value="newId"
                                :options="newPointOptions"
                                :placeholder="$t('New Delivery Point')"
                                clearable
                                @input="(val) => (newId = val)"
                            />
                        </td>
                        <td colspan="5"></td>
                        <td>
                            <IconButton icon="add-icon" @click="addMember" />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </CicPopup>
</template>

<script>
    import CicPopup from '@/components/CicPopup';
    import IconButton from '@/components/controls/IconButton';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicNumber from '@/components/inputs/CicNumber';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';

    export default {
        name: 'EditDeliveryPointGroup',
        components: {
            CicCheckbox,
            CicNumber,
            CicPopup,
            CicSelect,
            CicTextbox,
            IconButton,
        },
        props: {
            item: {
                type: Object,
                required: true,
            },
            points: {
                type: Object,
                required: true,
            },
        },
        emits: ['blur', 'change', 'input'],
        data() {
            return {
                editing: false,
                newId: null,
            };
        },
        computed: {
            newPointOptions() {
                const usedIds = this.item.members.filter((point) => point.pointId).map((point) => point.pointId);
                return Object.values(this.points)
                    .filter((point) => point.allowed && !usedIds.includes(point.pointId))
                    .map((point) => ({
                        value: point.pointId,
                        label: point.name,
                    }));
            },
        },
        created() {
            this.editing = !this.item.groupId || this.$store.hasRight('bEditDeliveryPointGroup');
        },
        methods: {
            addMember() {
                const members = JSON.parse(JSON.stringify(this.item.members));
                if (this.newId) {
                    members.push({ ...this.points[`_${this.newId.toString()}`], weight: 0 });
                } else {
                    members.push({ name: '', number: '', maxCalls: 0, weight: 0, allowed: true });
                }

                this.newId = null;
                this.updateFieldValue('members', members);
            },

            move(index, increment) {
                const members = JSON.parse(JSON.stringify(this.item.members));
                members.splice(index + increment, 0, members.splice(index, 1)[0]);
                this.updateFieldValue('members', members);
            },

            removeMember(index) {
                const members = JSON.parse(JSON.stringify(this.item.members));
                members.splice(index, 1);
                this.updateFieldValue('members', members);
            },

            updateFieldValue(field, val) {
                this.$emit('change', field, val);
            },

            updateMember(index, field, value) {
                const members = JSON.parse(JSON.stringify(this.item.members));
                members[index][field] = value;
                this.updateFieldValue('members', members);
            },
        },
    };
</script>
